/**
 * EmptyState — unified placeholder shown after data loads but is empty.
 *
 * Pattern: icon (gray) + bold title + optional gray hint + optional CTA.
 * Replaces ad-hoc `<div className="card p-8 text-center text-gray-400">…</div>` blocks.
 *
 * NEVER render this while data is loading — always gate on `!loading` to avoid
 * the "No X yet" flash before the API responds. Pair with <Skeleton.List /> for
 * the loading branch.
 */
import type { ReactNode } from 'react';

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  hint?: string;
  action?: ReactNode;
  /** Tighter padding for in-page sub-panels (Profile, Admin sub-lists). Default: roomy. */
  compact?: boolean;
  /** #661 — a card that a spec finds by its own id (`boxers-empty`, `error-state`).
   *  The list pages keep the default: `loading-states.spec.ts` counts it there. */
  testId?: string;
  /** `alert` for a failed load (`ErrorState`). */
  role?: 'alert' | 'status';
}

export default function EmptyState({ icon, title, hint, action, compact = false, testId = 'empty-state', role }: EmptyStateProps) {
  return (
    <div className={`card flex flex-col items-center text-center ${compact ? 'p-6' : 'p-10 sm:p-12'}`}
      role={role} data-testid={testId}>
      {icon && (
        <div className={`text-gray-300 ${compact ? 'mb-2' : 'mb-3'}`} aria-hidden="true">
          {icon}
        </div>
      )}
      <div className={`font-semibold text-gray-700 ${compact ? 'text-sm' : 'text-base'}`}>{title}</div>
      {hint && (
        <div className={`text-gray-400 mt-1 max-w-sm ${compact ? 'text-xs' : 'text-sm'}`}>{hint}</div>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
